import type { CityConfig } from "@/pages/city-template/types";

export const pendletonConfig: CityConfig = {
  vitalRecordsIcon: "ri-file-text-line",
  vitalRecordsLinks: [
    { icon: "ri-file-text-line", label: "Oregon Vital Records (Birth, Death, Marriage)", desc: "Request certified birth, death, and marriage certificates from Oregon Vital Records.", url: "https://www.oregon.gov/oha/PH/BirthDeathCertificates/Pages/index.aspx" },
    { icon: "ri-government-line", label: "Umatilla County Clerk Vital Records", desc: "Obtain vital records through the Umatilla County Clerk's office in Pendleton.", url: "https://www.umatillacounty.net/" },
    { icon: "ri-global-line", label: "VitalChek — Order Vital Records Online", desc: "Official online ordering partner of Oregon Vital Records for certified certificates.", url: "https://www.vitalchek.com/" },
  ],
  city: "Pendleton",
  state: "Oregon",
  stateAbbr: "OR",
  slug: "pendleton",
  route: "/cities/pendleton",
  title: "Emergency Passport Pendleton, OR | Expedited Passport Services Umatilla County",
  metaDescription: "Emergency passport and expedited passport services in Pendleton, OR — as fast as 24 hours. Serving Umatilla County, Hermiston, Milton-Freewater, and Eastern Oregon. A+ BBB rated registered State Dept. courier. Rush passport renewal for Pendleton area travelers.",
  h1: "Emergency & Expedited Passport Services in Pendleton, Oregon",
  heroSubtitle: "Passport expired before your Canada trip or international departure from PDX? Fast Passport Center helps Pendleton and Umatilla County travelers get expedited passports in as little as 24 hours. Eastern Oregon's trusted passport courier — start online, ship overnight.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Pendleton%20Oregon%20rolling%20wheat%20fields%20and%20hills%20at%20golden%20hour%20with%20wide%20open%20eastern%20Oregon%20landscape%2C%20warm%20amber%20and%20golden%20tones%20on%20vast%20agricultural%20terrain%2C%20dramatic%20sky%20with%20scattered%20clouds%2C%20clear%20visibility%20to%20distant%20mountain%20ridges%2C%20photorealistic%20rural%20Oregon%20countryside%2C%20no%20text%20or%20signs%2C%20peaceful%20and%20expansive%20western%20american%20scenery&width=1800&height=700&seq=pendleton-hero-01&orientation=landscape",
  heroImageAlt: "Rolling wheat fields and hills near Pendleton Oregon in golden light",
  geoPosition: "45.6721;-118.7886",
  geoPlacename: "Pendleton, Oregon",
  lastModified: "2026-06-14",
  regionalAgency: {
    name: "Seattle Passport Agency",
    address: "915 2nd Ave Suite 500, Seattle WA 98174",
    distance: "About 250 miles northwest — 4+ hours by car",
  },
  nearbyCitiesZips: [
    { name: "Pendleton", zips: "97801" },
    { name: "Hermiston", zips: "97838" },
    { name: "Milton-Freewater", zips: "97862" },
    { name: "Umatilla", zips: "97882" },
    { name: "Boardman", zips: "97818" },
    { name: "Pilot Rock", zips: "97868" },
    { name: "Walla Walla (WA)", zips: "99362" },
    { name: "College Place (WA)", zips: "99324" },
    { name: "Kennewick (WA)", zips: "99336" },
    { name: "Pasco (WA)", zips: "99301" },
    { name: "La Grande", zips: "97850" },
    { name: "Enterprise", zips: "97828" },
  ],
  benefits: [
    { icon: "ri-computer-line", title: "Start 100% Online", desc: "No office visit to our facility required. Complete your expedited passport order from Pendleton or anywhere in Umatilla County." },
    { icon: "ri-truck-line", title: "Overnight Shipping from Pendleton", desc: "Ship your documents overnight from FedEx or UPS in Pendleton. Most locations are open evenings and weekends." },
    { icon: "ri-shield-check-line", title: "Expert Document Review", desc: "Every application is professionally reviewed before submission — especially important for rural Eastern Oregon applicants far from a passport agency." },
    { icon: "ri-calendar-check-line", title: "Reserved Agency Access", desc: "We hold reserved submission slots at the nearest regional passport agency — bypassing the public appointment system entirely." },
  ],
  testimonials: [
    {
      name: "Bob F.",
      location: "Pendleton, OR",
      rating: 5,
      text: "Needed my passport renewed before a hunting trip to British Columbia. Called three post offices and couldn't get an appointment for weeks. Fast Passport Center took care of everything — shipped from Pendleton, passport back in 7 days. Absolutely use them again.",
      service: "Passport Renewal",
    },
    {
      name: "Sarah M.",
      location: "Hermiston, OR",
      rating: 5,
      text: "Emergency passport for my daughter. We had a family emergency in Mexico and needed passports fast. Fast Passport Center was incredible — they guided us through every step and had both passports back in 4 days. Saved our trip.",
      service: "Emergency Passport",
    },
    {
      name: "Jim P.",
      location: "Milton-Freewater, OR",
      rating: 5,
      text: "First passport application at 65. I had no idea what I was doing. Fast Passport Center's checklist was perfect — exactly what I needed to bring to the post office. Had my passport in 8 days.",
      service: "New Passport",
    },
  ],
  facilities: [
    {
      id: "pendleton-post",
      name: "Pendleton Post Office",
      type: "USPS Passport Acceptance",
      address: "104 SW Dorion Ave",
      city: "Pendleton",
      state: "OR",
      zip: "97801",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–5pm",
      distance: "Downtown Pendleton",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Primary passport acceptance facility for Umatilla County. Appointment required — scheduling can be limited. Call ahead to confirm availability and current hours.",
      icon: "ri-mail-line",
    },
    {
      id: "hermiston-post",
      name: "Hermiston Post Office",
      type: "USPS Passport Acceptance",
      address: "400 E Main St",
      city: "Hermiston",
      state: "OR",
      zip: "97838",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm",
      distance: "Hermiston — about 30 miles northwest",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Hermiston option for northern Umatilla County and Tri-Cities border area residents.",
      icon: "ri-mail-line",
    },
    {
      id: "umatilla-county-clerk",
      name: "Umatilla County Clerk",
      type: "County Passport Acceptance",
      address: "216 SE 4th St",
      city: "Pendleton",
      state: "OR",
      zip: "97801",
      phone: "541-278-6264",
      hours: "Mon–Fri 8am–5pm (by appointment)",
      distance: "Downtown Pendleton — County Courthouse",
      website: "https://www.umatillacounty.net/",
      websiteLabel: "Umatilla County Website",
      notes: "County clerk passport acceptance. Appointment required — call to verify current passport services. Often less backlogged than USPS.",
      icon: "ri-government-line",
    },
    {
      id: "walla-walla-post",
      name: "Walla Walla Post Office (WA)",
      type: "USPS Passport Acceptance",
      address: "128 N 2nd Ave",
      city: "Walla Walla",
      state: "WA",
      zip: "99362",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–5pm",
      distance: "Walla Walla, WA — about 45 miles north",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Walla Walla is a larger city with potentially better passport appointment availability. Washington applicants use the same U.S. passport forms.",
      icon: "ri-mail-line",
    },
    {
      id: "kennewick-post",
      name: "Kennewick Post Office (WA)",
      type: "USPS Passport Acceptance",
      address: "9401 W Clearwater Ave",
      city: "Kennewick",
      state: "WA",
      zip: "99336",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–5pm",
      distance: "Kennewick, WA (Tri-Cities) — about 55 miles north",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Tri-Cities area option with a larger passport acceptance operation and better appointment availability.",
      icon: "ri-mail-line",
    },
    {
      id: "milton-freewater-post",
      name: "Milton-Freewater Post Office",
      type: "USPS Passport Acceptance",
      address: "121 S Main St",
      city: "Milton-Freewater",
      state: "OR",
      zip: "97862",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Milton-Freewater — about 25 miles east",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Small branch near the Washington state border. Limited hours — call ahead before driving.",
      icon: "ri-mail-line",
    },
  ],
  faqs: [
    {
      q: "Can I get an emergency passport in Pendleton, Oregon?",
      a: "Yes. Through Fast Passport Center, Pendleton and Umatilla County travelers can get an emergency passport in as fast as 24 hours. Pendleton does not have a regional passport agency — the closest is in Seattle, over 250 miles away. Our registered courier service provides Pendleton travelers with the fastest available processing without requiring a cross-state drive.",
    },
    {
      q: "How do I expedite a passport from Pendleton, OR?",
      a: "Expediting a passport from Pendleton is straightforward with Fast Passport Center. Start your order online, receive your personalized checklist, and ship documents overnight from the FedEx or UPS location in Pendleton. We handle the expedited processing through reserved agency channels. Processing can be as fast as 2–9 business days.",
    },
    {
      q: "Where is the nearest passport agency to Pendleton, Oregon?",
      a: "The nearest regional passport agency to Pendleton is the Seattle Passport Agency, approximately 250 miles northwest (a 4+ hour drive each way). That agency only serves travelers with documented emergencies and travel within 14 days. Fast Passport Center is significantly faster and more convenient for most Umatilla County residents.",
    },
    {
      q: "Where can I get a passport in Pendleton, OR?",
      a: "Pendleton and Umatilla County have several passport acceptance facilities including the Pendleton Post Office, Umatilla County Clerk's office, Hermiston Post Office, and options in Walla Walla, WA. However, standard processing through these facilities takes 6–13 weeks. If you need your passport faster, use Fast Passport Center's expedited courier service.",
    },
    {
      q: "Can I get an expedited passport for a Canada hunting or fishing trip from Pendleton?",
      a: "Absolutely — this is one of our most common requests from Eastern Oregon. British Columbia hunting and fishing season draws thousands of Umatilla County hunters and anglers across the border each fall. If your passport is expired or expiring before your B.C. trip, start your expedited renewal immediately. Fast Passport Center can process in as little as 3 business days.",
    },
    {
      q: "What if I lost my passport before an international trip from Pendleton?",
      a: "If you lost your passport before an upcoming trip, act immediately. You'll need to complete Form DS-64 to report it lost and apply for a replacement. Fast Passport Center guides Pendleton travelers through this entire process and can expedite your replacement passport. Lost passports cannot be used even if found later — you need a new one.",
    },
    {
      q: "How long does standard passport processing take from Pendleton, OR?",
      a: "Standard USPS passport processing from Pendleton takes 6–13 weeks — and can be longer during the peak summer travel season (May–August). If your trip is within 10 weeks, you need expedited processing. Fast Passport Center offers Pendleton travelers processing as fast as 24 hours.",
    },
    {
      q: "Can I renew my passport from Pendleton without visiting an office?",
      a: "Yes. Most Pendleton-area adults renewing a passport (issued at age 16+ within the last 15 years) can do so entirely by mail — no in-person visit required. Start your order online with Fast Passport Center, receive your checklist, and ship overnight from the FedEx or UPS in Pendleton.",
    },
    {
      q: "Do I need a passport for Walla Walla, WA area travel?",
      a: "No — Washington state is a domestic destination. However, if you're planning to continue into British Columbia or other international destinations from the Walla Walla / Tri-Cities area, a valid passport is required. Many Pendleton-area residents also cross into BC via Oroville, WA — which requires a valid passport or enhanced driver's license.",
    },
    {
      q: "What airports do Pendleton passport holders use?",
      a: "The Pendleton Regional Airport (PDT) serves regional connections. Most international travel departs from Portland International Airport (PDX, about 210 miles west) or Seattle-Tacoma International (SEA, about 250 miles northwest). Both require a valid U.S. passport for international departures. If your passport isn't current, start your expedited renewal before booking flights.",
    },
  ],
  photoLocations: [
    { name: "Walgreens — Pendleton", address: "1412 SE Court Ave, Pendleton, OR 97801", hours: "Daily 8 AM – 9 PM" },
    { name: "Walmart — Pendleton", address: "1800 SE Court Pl, Pendleton, OR 97801", hours: "Daily 7 AM – 11 PM" },
    { name: "Safeway — Pendleton", address: "211 SE Court Ave, Pendleton, OR 97801", hours: "Daily 6 AM – 11 PM" },
    { name: "FedEx — Kennewick (WA)", address: "225 N Auburn St, Kennewick, WA 99336", hours: "Mon–Fri 8 AM – 7 PM" },
    { name: "Walgreens — Hermiston", address: "1445 N 1st St, Hermiston, OR 97838", hours: "Daily 8 AM – 9 PM" },
    { name: "The UPS Store — Walla Walla (WA)", address: "726 W Sprague St, Walla Walla, WA 99362", hours: "Mon–Fri 8 AM – 6 PM" },
    { name: "Walmart — Hermiston", address: "1425 N 1st St, Hermiston, OR 97838", hours: "Daily 7 AM – 11 PM" },
    { name: "FedEx Office — Kennewick (WA)", address: "7407 W Grandridge Blvd, Kennewick, WA 99336", hours: "Mon–Fri 7 AM – 9 PM" },
    { name: "CVS — Walla Walla (WA)", address: "350 E Rose St, Walla Walla, WA 99362", hours: "Daily 7 AM – 10 PM" },
    { name: "Safeway — Hermiston", address: "500 S Highway 395, Hermiston, OR 97838", hours: "Daily 6 AM – 11 PM" },
  ],
  dmvLocations: [
    {
      name: "Pendleton Driver & Motor Vehicle Services",
      address: "700 SE Nye Ave, Pendleton, OR 97801",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Primary Umatilla County DMV office. REAL ID, driver licenses, state IDs. Appointment recommended.",
      featured: true,
    },
    {
      name: "Hermiston Driver & Motor Vehicle Services",
      address: "Hermiston, OR 97838",
      hours: "Mon–Fri 8:30 AM – 5:00 PM (limited days)",
      note: "Hermiston DMV option. Hours are limited — call ahead to confirm.",
      featured: false,
    },
    {
      name: "Walla Walla DOL (WA)",
      address: "415 W Rose St, Walla Walla, WA 99362",
      hours: "Mon–Fri 8:30 AM – 4:30 PM",
      note: "Washington State Department of Licensing for Walla Walla area residents.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "PDT",
      name: "Pendleton Regional Airport",
      distance: "Local airport — regional connections via United Express to Portland",
      address: "900 SE Stukel Mountain Rd, Pendleton, OR 97801",
      note: "PDT offers regional connections to PDX and potentially other hubs. For international travel, passengers typically connect through PDX. Check current service — routes change seasonally.",
      flightUrl: "https://www.flypdx.com/",
      flightLabel: "PDX Airport Website",
      featured: false,
    },
    {
      code: "PDX",
      name: "Portland International Airport",
      distance: "Primary international airport — about 210 miles west via I-84",
      address: "7000 NE Airport Way, Portland, OR 97218",
      note: "PDX is the nearest major international airport for Pendleton travelers. The I-84 drive takes approximately 3.5 hours. PDX offers direct flights to Europe, Asia, Canada, and Latin America. Plan your passport needs well before your PDX departure.",
      flightUrl: "https://www.flypdx.com/",
      flightLabel: "PDX Airport Website",
      featured: true,
    },
  ],
  services: [
    {
      icon: "ri-time-line",
      title: "Emergency Passport",
      description: "Urgent passport for Pendleton and Eastern Oregon travelers — departing from PDX within days? We handle emergency processing through reserved agency channels so you don't have to drive 250 miles to Seattle.",
      bullets: ["As fast as 24 hours", "Reserved agency submission", "Lost passport emergency replacement"],
      badge: "Same-Day Available",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Expedited Passport Renewal",
      description: "Passport expired before your B.C. hunting trip or PDX international departure? Umatilla County's most popular service — start online and ship overnight from Pendleton with no office visit required.",
      bullets: ["No in-person visit for most renewals", "Ship from Pendleton FedEx or UPS", "Processing in as little as 3 business days"],
      badge: "Most Common",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-search-eye-line",
      title: "Lost Passport Replacement",
      description: "Lost your passport before a Pendleton-area international trip? We guide you through DS-64 and expedited replacement — no drive to Seattle required.",
      bullets: ["DS-64 form guidance", "Urgent replacement available", "Professional document review"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Child passport services for Umatilla County families — minors under 16 require an in-person acceptance visit with parental consent. We provide a complete checklist so you know exactly what to bring to the Pendleton Post Office.",
      bullets: ["Both parents typically required", "Clear checklist provided", "Expedited after acceptance step"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-add-line",
      title: "New Passport (First-Time)",
      description: "First passport application for Pendleton residents — we guide you through the in-person acceptance step at the Pendleton or Hermiston post office and handle expedited processing afterward.",
      bullets: ["Step-by-step checklist", "Acceptance clerk guidance", "Expedited after clerk visit"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-truck-line",
      title: "Registered Courier Service",
      description: "Fast Passport Center is a registered U.S. Department of State courier. We submit through reserved agency channels, giving Pendleton and Eastern Oregon travelers priority access that standard mail can't provide.",
      bullets: ["State Dept. registered courier", "Reserved daily submission slots", "Not impacted by government shutdowns"],
      badge: "Registered Courier",
      link: "https://fastpassportcenter.com/checkout/",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change & Damaged Passports",
      description: "Passport name changes after marriage or divorce, damaged passport replacement, and second passports for frequent international travelers — all available with expedited courier access.",
      bullets: ["Name change after marriage/divorce", "Damaged passport replacement", "Second passport for frequent travel"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for Pendleton, Oregon travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Expedited Passport Renewal", description: "Fast expedited passport renewal for Pendleton and Umatilla County, OR residents." },
    { name: "Passport Courier Service", description: "Registered U.S. Department of State courier passport service serving Pendleton and Eastern Oregon." },
    { name: "Lost Passport Replacement", description: "Emergency passport replacement for Pendleton travelers with lost or stolen passports." },
    { name: "Child Passport", description: "Expedited child passport services for Umatilla County families." },
    { name: "New Passport Application", description: "First-time passport application assistance for Pendleton, OR residents." },
  ],
  mistakes: [
    { icon: "ri-time-line", title: "Waiting Too Long to Apply", description: "Eastern Oregon hunters and anglers often check passport expiration only when booking a B.C. hunting license — sometimes just 3–4 weeks before their trip. Standard processing takes 6–13 weeks. Don't let this happen to you." },
    { icon: "ri-camera-off-line", title: "Passport Photo Issues", description: "Shadows, incorrect background, or poor photo quality cause the most preventable application delays. We send exact photo specifications — use a Walgreens or FedEx for consistent results." },
    { icon: "ri-file-damage-line", title: "Incomplete or Wrong Forms", description: "A single missing signature or wrong date sends your application back. Fast Passport Center reviews every document before submission — critical for remote applicants far from a passport agency." },
    { icon: "ri-pen-nib-line", title: "Wrong Birth Certificate Type", description: "Some birth certificates from rural Oregon don't meet passport requirements. We tell you exactly what's needed for your specific situation before you drive to the post office." },
    { icon: "ri-calendar-close-line", title: "Relying on Post Office Appointments", description: "Pendleton post office passport appointments can be weeks out. If B.C. hunting season opens in 3 weeks, you don't have time to wait. Use Fast Passport Center's courier service instead." },
    { icon: "ri-map-pin-time-line", title: "Name Mismatch at the Border", description: "Your passport must exactly match your airline ticket and border crossing documents. Middle names, hyphens, and name changes after marriage cause problems at PDX and the Canadian border." },
  ],
  steps: [
    { number: "01", icon: "ri-computer-line", title: "Start Your Order Online", description: "Choose your processing speed based on your travel date. Complete your secure order from Pendleton in minutes." },
    { number: "02", icon: "ri-file-list-3-line", title: "Receive Your Personalized Checklist", description: "We send your document checklist immediately — exactly what you need for your Pendleton-area application." },
    { number: "03", icon: "ri-send-plane-line", title: "Ship Overnight from Pendleton", description: "Drop off at FedEx or UPS in Pendleton or Hermiston. Most are open evenings." },
    { number: "04", icon: "ri-building-2-line", title: "Reserved Agency Submission", description: "We submit through our reserved passport agency slots — bypassing public appointment backlogs." },
    { number: "05", icon: "ri-passport-line", title: "Passport Delivered to You", description: "Your completed passport is shipped back to your address in Pendleton or anywhere in Umatilla County." },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Life-or-death emergencies only",
        "Seattle agency — 250 miles from Pendleton",
        "Strict eligibility requirements",
        "All-day wait at agency",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround for Eastern Oregon",
        "Expert document review included",
        "Dedicated agent assigned",
        "Daily status updates",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Faster than USPS self-service",
        "Good for planned international travel",
        "Document review included",
        "Dedicated agent assigned",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access",
        "Limited appointment availability in Pendleton",
        "No document review",
        "No dedicated agent",
        "Updates by mail only",
        "Not suitable for travel within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — Seattle agency, 250 miles away", fpc: "No appointment needed to start" },
    { feature: "In-Person Visit", agency: "Must drive to Seattle (4+ hours each way)", fpc: "Ship overnight from Pendleton or Hermiston" },
    { feature: "Processing Speed", agency: "Emergency travel only — strict eligibility", fpc: "As fast as 24 hours — multiple speed tiers" },
    { feature: "Document Review", agency: "None — errors cause rejection", fpc: "Professional review included" },
    { feature: "Rural Support", agency: "No support for remote applicants", fpc: "Dedicated agent for every case" },
    { feature: "Government Shutdowns", agency: "Operations may be suspended", fpc: "Not affected" },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Acceptance Locations", href: "#acceptance-facilities" },
    { label: "vs. Regional Agency", href: "#comparison" },
    { label: "FAQ", href: "#faq" },
    { label: "Local Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Emergency passport services in La Grande", route: "/cities/la-grande" },
    { name: "Expedited passport help in Portland", route: "/cities/portland" },
    { name: "Rush passport renewal in The Dalles", route: "/cities/the-dalles" },
    { name: "Fast passport services in Bend", route: "/cities/bend" },
    { name: "Oregon expedited passport guide", route: "/states/oregon" },
  ],
  fpcOfficeAddress: "5608 17th Avenue NW, Seattle, WA 98107",
  fpcOfficeNote: "After starting your order online, we send your personalized document checklist and shipping instructions. Ship overnight from Pendleton FedEx — no in-person visit to our facility required for most services.",
  fpcOfficeDistance: "Serving Pendleton and all of Umatilla County remotely",
  serviceAreaIntro: "Whether you are in Pendleton, Hermiston, Milton-Freewater, Umatilla, or anywhere in Umatilla County, Fast Passport Center makes expedited passport services accessible from rural Eastern Oregon.",
  nearbyCitiesIntro: "We serve all Umatilla County zip codes plus the Washington side of the border area. Ship overnight and your documents reach our processing center by morning.",
  howItWorksIntro: "Getting an expedited passport from Pendleton is simpler than you think. Start online, ship from your local FedEx or UPS, and we handle everything else.",
  mistakesIntro: "These are the most common passport mistakes from Eastern Oregon and Umatilla County travelers. Fast Passport Center reviews your documents before submission to prevent delays.",
  testimonialsIntro: "Real reviews from Pendleton, Hermiston, and Eastern Oregon travelers who needed their passports fast.",
  faqIntro: "The most common questions Pendleton and Umatilla County residents ask about emergency and expedited passport services.",
  statePageRoute: "/states/oregon",
  statePageName: "Oregon",
  travelTiming: {
    title: "Travel Timing & Passport Urgency for Pendleton, Oregon Residents",
    intro: "Pendleton's geography puts it at the crossroads of Eastern Oregon outdoor recreation, Pacific Rim agricultural trade, and easy access to British Columbia. Understanding these local travel patterns helps us serve Umatilla County passport needs — especially the predictable urgencies that come with hunting season and international business travel.",
    cards: [
      {
        icon: "ri-map-pin-2-line",
        title: "British Columbia Hunting & Fishing Season",
        subtitle: "B.C. border via Oroville/Osoyoos WA, Aug–Nov",
        description: "Eastern Oregon and Umatilla County send hundreds of hunters and anglers across the border to British Columbia every fall. Elk, mule deer, moose, steelhead, and salmon hunting and fishing draw Pendleton-area residents north every August through November. The predictable problem: hunters often discover their passport expired only when booking their B.C. hunting license online — sometimes just 2–4 weeks before their trip. Standard post office processing simply cannot meet these timelines.",
        tip: "Check your passport expiration every spring before B.C. hunting and fishing season. If it expires within 12 months, renew it now. Getting a B.C. hunting license is the wrong time to discover your passport expired 3 months ago.",
      },
      {
        icon: "ri-flight-takeoff-line",
        title: "PDX Airport — 210 Miles West via I-84",
        subtitle: "About 3.5 hours from Pendleton",
        description: "Portland International Airport is the primary gateway for Pendleton and Umatilla County travelers heading internationally. The I-84 corridor makes PDX accessible for early morning departures if you plan ahead. PDX serves Europe, Asia, Canada, and Latin America with direct flights. Many Eastern Oregon residents book trips without checking passport expiration — and call us 2 weeks before departure when they realize they have a problem.",
        tip: "Check passport expiration as soon as you book international travel through PDX. Many countries require 6 months validity beyond your return date — your passport may technically be valid but still get rejected abroad.",
      },
      {
        icon: "ri-leaf-line",
        title: "Pendleton Round-Up & Agricultural Export",
        subtitle: "Pacific Rim agricultural trade, Japan, South Korea",
        description: "Umatilla County is one of Oregon's most important agricultural regions — wheat, peas, corn, and cattle. Export businesses frequently travel to Japan, South Korea, and Taiwan for trade shows and negotiations. The Pendleton Round-Up and associated events also bring international visitors and create international networking that sometimes leads to spontaneous travel opportunities for Pendleton residents.",
        tip: "Agricultural trade travel to Japan and South Korea often requires pre-trip visa applications. Both countries require a valid passport — if yours has less than 12 months remaining, renew before applying for any visas.",
      },
      {
        icon: "ri-graduation-cap-line",
        title: "Walla Walla University & Whitman College Travel",
        subtitle: "Study abroad from Walla Walla, WA (45 miles north)",
        description: "The Walla Walla area — easily accessible from Pendleton — is home to Whitman College and Walla Walla University, both of which send students abroad for semester programs and summer studies. Parents of these students frequently need child passports or renewal assistance when their student's passport expires mid-program. Many Pendleton-area families send children to these schools, creating a consistent passport demand pattern each year.",
        tip: "College students: check your passport expiration before each academic year. Study-abroad programs often have non-refundable deposits — discovering an expired passport after committing to a program in France or Japan is an expensive problem.",
      },
    ],
    callout: {
      icon: "ri-map-2-line",
      title: "250 Miles from Seattle — We Come to You",
      description: "Driving 250 miles to the Seattle passport agency is not a realistic option for most Pendleton and Eastern Oregon residents — especially for hunters with fixed season dates, business travelers with set itineraries, and families who can't take a day off work. Fast Passport Center was designed for exactly this situation. Start your order online, ship from the Pendleton FedEx, and we handle everything from there.",
    },
  },
  destinations: {
    title: "Where Pendleton Travelers Go — And Why Passports Get Urgent",
    intro: "These are the most common destinations and travel patterns we see from Umatilla County clients. Each one carries a predictable passport urgency that we've helped hundreds of Eastern Oregon travelers navigate.",
    stats: [
      { stat: "Aug–Nov", label: "B.C. hunting and fishing season — peak period for Pendleton emergency passport requests" },
      { stat: "2–4 Wks", label: "typical notice when Eastern Oregon hunters discover expired passports before their B.C. trip" },
      { stat: "210 Mi", label: "from Pendleton to PDX — the nearest international airport" },
      { stat: "6 Months", label: "minimum passport validity required by Mexico, Japan, and most EU countries" },
    ],
    destinations: [
      {
        name: "British Columbia, Canada",
        context: "Hunting, fishing, and outdoor recreation — the single most common destination for Pendleton and Umatilla County passport holders",
        urgency: "B.C. hunting and fishing season creates an Aug–Nov spike in urgent passport renewals for Eastern Oregon residents",
        icon: "ri-map-pin-2-line",
      },
      {
        name: "Japan & South Korea",
        context: "Pacific Rim agricultural trade shows, grain and cattle export negotiations, and Pendleton Round-Up international business connections",
        urgency: "Trade travel to Japan often has 2–4 week notice; Japan visa application requires valid passport",
        icon: "ri-map-pin-2-line",
      },
      {
        name: "Mexico",
        context: "Winter family vacations, agricultural research trips to Mexican farm operations, and retirement scouting for Eastern Oregon retirees",
        urgency: "Mexico requires 6 months passport validity beyond return date; winter bookings often confirmed just 3–4 weeks out",
        icon: "ri-map-pin-2-line",
      },
      {
        name: "Europe (Germany, France, UK)",
        context: "Agricultural machinery trade shows (AGRITECHNICA in Germany), wine and spirits travel, and European heritage trips for Eastern Oregon families",
        urgency: "AGRITECHNICA and trade show registrations often finalized weeks before departure; ETIAS required soon",
        icon: "ri-map-pin-2-line",
      },
      {
        name: "Alaska",
        context: "Hunting and fishing from Eastern Oregon — some Pendleton residents drive to Bellingham or Seattle to catch Alaska ferries or cruises",
        urgency: "Alaska cruise and ferry bookings often confirmed 4–8 weeks before departure",
        icon: "ri-map-pin-2-line",
      },
      {
        name: "Costa Rica & Latin America",
        context: "Eco-tourism and agricultural research trips to Central and South American operations from Umatilla County agricultural community",
        urgency: "Costa Rica requires 6 months passport validity; winter bookings often confirmed just weeks in advance",
        icon: "ri-map-pin-2-line",
      },
    ],
    crossLinksTitle: "Explore Other Eastern Oregon Service Areas",
    crossLinksDesc: "We serve travelers throughout Eastern Oregon and the Columbia Gorge with the same expedited passport processing expertise.",
    crossLinks: [
      { name: "La Grande", route: "/cities/la-grande" },
      { name: "The Dalles", route: "/cities/the-dalles" },
      { name: "Bend", route: "/cities/bend" },
    ],
  },
};